import { useGetOnboardingStateQuery, type OnboardingStage } from '@/redux/internal-services/onboardingApi';
import { useAppSelector } from './useAppSelector';
import { useIsTenantAdmin } from './useIsTenantAdmin';

interface OnboardingRoute {
  /** Current onboarding stage, or 'loading' until the backend state resolves. */
  stage: OnboardingStage | 'loading';
  /** First outstanding wizard step to resume at (only meaningful when stage === 'setup'). */
  resumeStep: string | null;
  /** Path the user should be routed to, or null when onboarding is done (→ settings until dashboard is built). */
  target: string | null;
  isLoading: boolean;
}

const RESOLVED = ['completed', 'skipped'];

// Plan selection is sequenced AFTER these guided-setup steps. The user verifies their phone
// first, then chooses a plan, then completes the rest of setup. Keep in sync with the wizard's
// in-step plan diversion (SetupWizardPage.goNext).
const PRE_PLAN_STEPS = ['phone_verification'];

/**
 * Single source of truth for onboarding routing. Reads the backend onboarding state machine
 * (`GET /api/v1/onboarding/state`) and maps the returned stage to the path the user belongs on.
 *
 * Why backend-driven: onboarding progress is recorded at the tenant level, so a user who
 * closes the app mid-flow — or signs in from another device — is always resumed at the right
 * place. No localStorage, no client-side inference.
 *
 * Only Tenant Admins go through onboarding; everyone else resolves to `stage: 'done'`,
 * `target: null`. While the state is still loading, `isLoading` is true so callers can defer
 * the redirect instead of flashing past to the dashboard.
 */
export const useOnboardingRoute = (): OnboardingRoute => {
  const isAuthenticated = useAppSelector((s) => s.sessionState.isAuthenticated);
  const tenantId = useAppSelector((s) => s.sessionState.tenantId);
  const subscriptionStatus = useAppSelector((s) => s.sessionState.subscriptionStatus);
  const userOnboardingCompleted = useAppSelector((s) => s.sessionState.userOnboardingCompleted);
  const samWelcomeShown = useAppSelector((s) => s.sessionState.samWelcomeShown);
  const isTenantAdmin = useIsTenantAdmin();

  // Per-user onboarding (profile + voice) applies to ALL authenticated users who haven't
  // completed it — both tenant admins finishing the wizard and invited users on first login.
  const userOnboardingTarget =
    isAuthenticated && !userOnboardingCompleted ? '/onboarding/profile' : null;

  // Skip the tenant checklist fetch entirely once SAM welcome has fired — nothing downstream
  // will read it and it just noise-triggers the API. `shouldCheck` stays a plain expression so
  // the hook call remains unconditional (Rules of Hooks).
  const shouldCheck = isAuthenticated && isTenantAdmin && !!tenantId && !samWelcomeShown;
  const { data, isLoading, isError } = useGetOnboardingStateQuery(undefined, { skip: !shouldCheck });

  // Once the SAM welcome has fired for this user (persistent flag on tenant_memberships,
  // flipped on the first-ever SAM turn) treat onboarding as fully done regardless of what's
  // still outstanding on the tenant/user checklist — a returning member must never be bounced
  // back through plans/setup/profile just because a per-user step (e.g. voice) was skipped.
  if (isAuthenticated && samWelcomeShown) {
    return { stage: 'done', resumeStep: null, target: null, isLoading: false };
  }

  if (!shouldCheck) {
    return { stage: 'done', resumeStep: null, target: userOnboardingTarget, isLoading: false };
  }
  // Still fetching — defer redirect so callers don't flash past to the wrong page.
  if (isLoading) return { stage: 'loading', resumeStep: null, target: null, isLoading: true };
  // API error or missing data — don't hang forever; fall through to dashboard/user-onboarding.
  if (isError || !data?.data) return { stage: 'done', resumeStep: null, target: userOnboardingTarget, isLoading: false };

  const { stage, resume_step, steps } = data.data!;

  // Pre-plan steps (phone verification) come before plan selection. They're satisfied once
  // resolved, or if simply not part of the configured flow.
  const prePlanResolved = steps
    .filter((s) => PRE_PLAN_STEPS.includes(s.step))
    .every((s) => RESOLVED.includes(s.status));

  // Ordering: pre-plan setup → plan selection → remaining setup → settings → dashboard.
  let target: string | null;
  if (stage === 'setup' && !prePlanResolved) {
    target = '/onboarding/setup';
  } else if (subscriptionStatus === 'not_selected') {
    target = '/onboarding/plans';
  } else if (stage === 'setup') {
    target = '/onboarding/setup';
  } else if (stage === 'settings') {
    // Company steps are done but settings hasn't been visited yet. That milestone only matters
    // for the tenant admin who ran the original setup — anyone else (or that same admin with a
    // pending profile/voice step) belongs on the minimal personal-onboarding page, never back in
    // the full company wizard, since there's nothing left there for them to do.
    target = userOnboardingCompleted ? '/settings/tenant' : userOnboardingTarget;
  } else {
    // stage === 'done'. Company setup is fully resolved — only a pending personal profile/voice
    // step (if any) remains, which userOnboardingTarget already points at (or null when done).
    target = userOnboardingTarget;
  }

  return { stage, resumeStep: resume_step, target, isLoading: false };
};
